import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getTransactions } from '@/features/wallet/services/walletService';
import { ErrorBanner } from '@/shared/ui/ErrorBanner';
import { Button, Card, CardHeader, EmptyState, Segmented, Spinner } from '@/shared/ui';
import { safeErrorMessage } from '@/shared/utils/errors';
import { transactionLabel } from '@/features/wallet/utils/transactions';
import { formatSignedCredits, formatTransactionDate } from '@/shared/utils/format';
import type { WalletBalance, WalletTransaction, WalletTransactionKind } from '@/shared/types';

const PAGE_SIZE = 50;

type TypeFilter = 'all' | WalletTransactionKind;

const TYPE_FILTERS: { key: TypeFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'topup', label: 'Top-ups' },
  { key: 'charge', label: 'Video generation' },
  { key: 'adjustment', label: 'Adjustments' },
];

// Inclusive local-day bounds, so picking the same From/To date matches
// everything that happened that day rather than just midnight.
function startOfDay(dateStr: string): number {
  return new Date(`${dateStr}T00:00:00`).getTime();
}

function endOfDay(dateStr: string): number {
  return new Date(`${dateStr}T23:59:59.999`).getTime();
}

export function WalletActivityTable({ balance }: { balance: WalletBalance | null }) {
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const balanceKey = balance
    ? `${balance.balanceCredits}|${balance.onHoldCredits}|${balance.totalCredits}`
    : 'unloaded';

  useEffect(() => {
    let active = true;
    if (!hasLoadedRef.current) setLoading(true);
    setError(null);
    getTransactions({ limit: PAGE_SIZE })
      .then((page) => {
        if (!active) return;
        const firstLoad = !hasLoadedRef.current;
        hasLoadedRef.current = true;
        /* This effect re-runs on any balance movement — a generation settling,
           a top-up landing — which used to *replace* the list wholesale: three
           presses of "Load more" collapsed back to 50 rows, mid-read, on no
           action of the user's. Merge instead: fresh rows go on top (new
           transactions are what moved the balance), rows the fresh page also
           carries are taken from it (statuses refresh), and everything older
           that the user paged into stays. The cursor still points past the
           oldest retained row, so "Load more" continues from where it truly is
           — only the first load may set it. */
        setTransactions((prev) => {
          if (prev.length === 0) return page.transactions;
          const fresh = new Set(page.transactions.map((t) => t.id));
          return [...page.transactions, ...prev.filter((t) => !fresh.has(t.id))];
        });
        if (firstLoad) setNextCursor(page.nextCursor);
      })
      .catch((err: unknown) => {
        if (!active) return;
        setError(safeErrorMessage(err, 'Unable to load your activity.'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [balanceKey]);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const page = await getTransactions({ limit: PAGE_SIZE, before: nextCursor });
      setTransactions((prev) => [...prev, ...page.transactions]);
      setNextCursor(page.nextCursor);
    } catch (err) {
      setError(safeErrorMessage(err, 'Unable to load more activity.'));
    } finally {
      setLoadingMore(false);
    }
  };

  const fromMs = fromDate ? startOfDay(fromDate) : null;
  const toMs = toDate ? endOfDay(toDate) : null;

  const dateFiltered = useMemo(() => {
    if (fromMs == null && toMs == null) return transactions;
    return transactions.filter((tx) => {
      const ts = new Date(tx.occurredAt).getTime();
      if (Number.isNaN(ts)) return false;
      if (fromMs != null && ts < fromMs) return false;
      if (toMs != null && ts > toMs) return false;
      return true;
    });
  }, [transactions, fromMs, toMs]);

  // Counts reflect the date filter (so a picked range shows what's actually in
  // it) but not the type filter itself — otherwise picking "Top-ups" would
  // zero out every other pill's count.
  const counts = useMemo(() => {
    const result: Record<TypeFilter, number> = {
      all: dateFiltered.length,
      topup: 0,
      charge: 0,
      adjustment: 0,
    };
    for (const tx of dateFiltered) result[tx.kind] += 1;
    return result;
  }, [dateFiltered]);

  const visible = useMemo(() => {
    if (typeFilter === 'all') return dateFiltered;
    return dateFiltered.filter((tx) => tx.kind === typeFilter);
  }, [dateFiltered, typeFilter]);

  const filtersActive = typeFilter !== 'all' || fromDate !== '' || toDate !== '';
  const clearFilters = () => {
    setTypeFilter('all');
    setFromDate('');
    setToDate('');
  };

  return (
    <Card padding="lg" className="wallet-activity">
      <CardHeader title="Activity" headingLevel={2} />

      {loading ? (
        <div className="wallet-activity__loading">
          <Spinner size={26} label="Loading activity" />
        </div>
      ) : error && transactions.length === 0 ? (
        <ErrorBanner message={error} />
      ) : transactions.length === 0 ? (
        <EmptyState
          title="No activity yet"
          hint="Your top-ups and video charges will show up here."
        />
      ) : (
        <>
          <div className="wallet-activity__toolbar">
            {/* Was a hand-rolled tablist on `.filter-pill`, whose rules the browse
                rebuild deleted from legacy.css along with the last browse use —
                leaving four unstyled buttons here and the count jammed onto the
                label ("Top-ups1"). Segmented is the primitive for a small set of
                mutually exclusive choices, and it brings the roving tabindex and
                arrow keys that `role="tab"` implies and the hand-rolled version
                never had. */}
            <Segmented
              label="Filter activity by type"
              size="sm"
              value={typeFilter}
              onChange={setTypeFilter}
              options={TYPE_FILTERS.map((f) => ({
                value: f.key,
                label: (
                  <>
                    {f.label}
                    <span className="wallet-activity__count">{counts[f.key]}</span>
                  </>
                ),
              }))}
            />

            <div className="wallet-activity__date-filter">
              <label className="wallet-activity__date-field">
                <span>From</span>
                <input
                  type="date"
                  value={fromDate}
                  max={toDate || undefined}
                  onChange={(e) => setFromDate(e.target.value)}
                  aria-label="Filter activity from date"
                />
              </label>
              <label className="wallet-activity__date-field">
                <span>To</span>
                <input
                  type="date"
                  value={toDate}
                  min={fromDate || undefined}
                  onChange={(e) => setToDate(e.target.value)}
                  aria-label="Filter activity to date"
                />
              </label>
              {filtersActive && (
                <button
                  type="button"
                  className="wallet-activity__clear-filters"
                  onClick={clearFilters}
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>

          {visible.length === 0 ? (
            <EmptyState compact title="No matching activity" hint="Try a different date range or type." />
          ) : (
            <div className="wallet-activity__table-wrap">
              <table className="wallet-activity__table">
                <thead>
                  <tr>
                    <th scope="col">Date</th>
                    <th scope="col">Type</th>
                    <th scope="col">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((tx) => (
                    <tr key={tx.id}>
                      <td className="wallet-activity__date-cell">{formatTransactionDate(tx.occurredAt)}</td>
                      <td className="wallet-activity__label-cell">
                        {tx.kind === 'charge' && tx.reference ? (
                          <Link to={`/videos/${tx.reference}`}>{transactionLabel(tx)}</Link>
                        ) : (
                          transactionLabel(tx)
                        )}
                      </td>
                      <td
                        className={`wallet-activity__amount-cell${tx.amountCredits > 0 ? ' wallet-activity__amount-cell--pos' : ''
                          }`}
                      >
                        {formatSignedCredits(tx.amountCredits)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
          {nextCursor && (
            <Button className="wallet-activity__more" onClick={loadMore} loading={loadingMore}>
              {loadingMore ? 'Loading…' : 'Load more'}
            </Button>
          )}
        </>
      )}
    </Card>
  );
}
